import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@veritrellis/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import type { WorkspaceRole } from "@veritrellis/shared/roles";
import { workspaceRoleRank } from "@veritrellis/shared/roles";
import { PageHeader } from "../components/PageHeader.js";
import { MemberTableRow, type MemberRow } from "../components/members/MemberTableRow.js";
import { MembersInvitePanel } from "../components/members/MembersInvitePanel.js";
import { apiFetch } from "../lib/api.js";
import {
  workspaceRoleBadgeVariant,
  workspaceRoleDescriptions,
  workspaceRolesOrdered
} from "../lib/workspaceRolePresentation.js";
import { useAdminMe } from "../hooks/useAdminMe.js";
import { roleAtLeast, useCurrentRole } from "../hooks/useCurrentRole.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";

type RoleFilter = "all" | WorkspaceRole;

const sortMembers = (members: MemberRow[]): MemberRow[] =>
  [...members].sort((a, b) => {
    const dr = workspaceRoleRank[b.role] - workspaceRoleRank[a.role];
    if (dr !== 0) {
      return dr;
    }
    const ae = a.email ?? a.user_id;
    const be = b.email ?? b.user_id;
    return ae.localeCompare(be);
  });

const assignableRolesForActor = (actor: WorkspaceRole | null): WorkspaceRole[] => {
  if (actor === "owner") {
    return ["owner", "admin", "developer", "approver", "viewer", "auditor"];
  }
  if (actor === "admin") {
    return ["admin", "developer", "approver", "viewer", "auditor"];
  }
  return [];
};

const countByRole = (members: MemberRow[]): Partial<Record<WorkspaceRole, number>> => {
  const counts: Partial<Record<WorkspaceRole, number>> = {};
  for (const m of members) {
    counts[m.role] = (counts[m.role] ?? 0) + 1;
  }
  return counts;
};

export const SettingsMembersPage = () => {
  const { workspaceId, isLoading: widLoading } = useWorkspaceId();
  const me = useAdminMe();
  const { role: myRole } = useCurrentRole();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");

  const q = useQuery({
    queryKey: ["members", workspaceId],
    enabled: workspaceId !== null,
    queryFn: async () => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/members`);
      if (!res.ok) {
        throw new Error(`members_${String(res.status)}`);
      }
      return (await res.json()) as { members: MemberRow[] };
    }
  });

  const canManage = roleAtLeast(myRole, "admin");
  const myUserId = me.data?.user.id ?? null;
  const assignableRoles = useMemo(() => assignableRolesForActor(myRole), [myRole]);

  const allMembers = useMemo(() => sortMembers(q.data?.members ?? []), [q.data?.members]);
  const roleCounts = useMemo(() => countByRole(allMembers), [allMembers]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allMembers.filter((m) => {
      if (roleFilter !== "all" && m.role !== roleFilter) {
        return false;
      }
      if (term.length === 0) {
        return true;
      }
      const haystack = [m.email, m.name, m.user_id, m.role]
        .filter((v): v is string => v !== null && v.length > 0)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [allMembers, search, roleFilter]);

  const roleFilterOptions = useMemo(() => {
    const withMembers = workspaceRolesOrdered.filter((r) => (roleCounts[r] ?? 0) > 0);
    return withMembers;
  }, [roleCounts]);

  if (widLoading || me.isLoading) {
    return (
      <div className="admin-page-stack">
        <Skeleton style={{ height: 48, width: "40%" }} />
        <Skeleton style={{ height: 280, width: "100%" }} />
      </div>
    );
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }
  if (q.isLoading) {
    return (
      <div className="admin-page-stack">
        <Skeleton style={{ height: 48, width: "40%" }} />
        <Skeleton style={{ height: 280, width: "100%" }} />
      </div>
    );
  }
  if (q.isError) {
    return <p className="text-sm text-destructive">Failed to load members.</p>;
  }

  return (
    <div className="admin-page-stack">
      <PageHeader
        title="Members"
        description="Control who can access this workspace and what they can do. Changes apply immediately and are written to the product audit log."
        actions={
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {roleAtLeast(myRole, "admin") ? (
              <Button variant="outline" size="sm" asChild>
                <Link to="/settings/access">Roles & permissions</Link>
              </Button>
            ) : null}
            {roleAtLeast(myRole, "admin") ? (
              <Button variant="outline" size="sm" asChild>
                <Link to="/settings/approver-groups">Approver groups</Link>
              </Button>
            ) : null}
          </div>
        }
      />

      {!canManage ? (
        <Alert>
          <AlertTitle>View only</AlertTitle>
          <AlertDescription>
            Your role is <strong>{myRole ?? "unknown"}</strong>. Only workspace admins and owners
            can invite people or change roles.
          </AlertDescription>
        </Alert>
      ) : null}

      {canManage && assignableRoles.length > 0 ? (
        <MembersInvitePanel workspaceId={workspaceId} inviteRoles={assignableRoles} />
      ) : null}

      <section className="admin-panel" aria-labelledby="members-table-heading">
        <div className="admin-panel-header">
          <h2 id="members-table-heading" className="admin-panel-title">
            Team directory
          </h2>
          <span className="admin-panel-meta">{allMembers.length} members</span>
        </div>

        <div className="admin-filter-toolbar">
          <div className="admin-filter-pills" role="group" aria-label="Filter by role">
            <button
              type="button"
              className={`admin-filter-pill${roleFilter === "all" ? " admin-filter-pill-active" : ""}`}
              onClick={() => setRoleFilter("all")}
              aria-pressed={roleFilter === "all"}
            >
              All ({allMembers.length})
            </button>
            {roleFilterOptions.map((r) => (
              <button
                key={r}
                type="button"
                className={`admin-filter-pill${roleFilter === r ? " admin-filter-pill-active" : ""}`}
                onClick={() => setRoleFilter(r)}
                aria-pressed={roleFilter === r}
              >
                {r} ({roleCounts[r] ?? 0})
              </button>
            ))}
          </div>
          <div className="admin-search-field">
            <label className="admin-search-label" htmlFor="members-search">
              Search
            </label>
            <Input
              id="members-search"
              type="search"
              placeholder="Name, email, or user ID…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          </div>
        </div>

        {allMembers.length > 0 ? (
          <div className="admin-count-strip">
            <span>
              Showing <strong>{filtered.length}</strong> of <strong>{allMembers.length}</strong>
            </span>
            {workspaceRolesOrdered.map((r) => {
              const n = roleCounts[r] ?? 0;
              if (n === 0) {
                return null;
              }
              return (
                <span key={r}>
                  <strong>{n}</strong> {r}
                  {n === 1 ? "" : "s"}
                </span>
              );
            })}
          </div>
        ) : null}

        <div className="admin-panel-body">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>User ID</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="admin-members-role-col">Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <div className="admin-empty-state">
                      <p className="admin-empty-state-text">
                        {allMembers.length === 0
                          ? "No members yet"
                          : "No members match your filters"}
                      </p>
                      {allMembers.length === 0 && canManage ? (
                        <p className="admin-form-field-hint">
                          Invite someone above to get started.
                        </p>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((m) => (
                  <MemberTableRow
                    key={m.user_id}
                    member={m}
                    workspaceId={workspaceId}
                    canManage={canManage}
                    myUserId={myUserId}
                    myRole={myRole}
                    assignableRoles={assignableRoles}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      <details className="admin-panel admin-panel-disclosure">
        <summary className="admin-panel-disclosure-summary">
          <span className="admin-panel-title">Role reference</span>
          <span className="admin-panel-disclosure-hint">Capabilities by workspace role</span>
        </summary>
        <div className="admin-panel-body-padded admin-panel-disclosure-body">
          <div className="admin-role-grid">
            {workspaceRolesOrdered.map((r) => (
              <div key={r} className="admin-role-card">
                <div className="admin-role-card-head">
                  <Badge variant={workspaceRoleBadgeVariant(r)}>{r}</Badge>
                  {(roleCounts[r] ?? 0) > 0 ? (
                    <span className="admin-role-card-count">{roleCounts[r]} in workspace</span>
                  ) : null}
                </div>
                <p className="admin-role-card-desc">{workspaceRoleDescriptions[r]}</p>
              </div>
            ))}
          </div>
        </div>
      </details>
    </div>
  );
};
